/**
 * Smart API URL detection for both local and production environments
 * This utility ensures the frontend works seamlessly in both development and production
 */

export const getApiUrl = () => {
  // 1. Check if VITE_API_URL is explicitly set (for production builds)
  if (import.meta.env.VITE_API_URL) {
    console.log(`🔗 Using explicit API URL: ${import.meta.env.VITE_API_URL}`);
    return import.meta.env.VITE_API_URL;
  }
  
  // 2. Detect environment based on current location
  const currentHost = window.location.hostname;
  const currentProtocol = window.location.protocol;
  const currentPort = window.location.port;
  
  // 3. Local development detection
  if (isLocalEnvironment(currentHost)) {
    // If we're running on Vite dev server (port 5173), use proxy
    if (currentPort === '5173') {
      // Vite proxy will handle /api and /ws routes
      const localUrl = `${currentProtocol}//${currentHost}:${currentPort}`;
      console.log(`🔗 Using Vite dev server with proxy: ${localUrl}`);
      return localUrl;
    }
    
    // Direct connection to backend
    const backendPort = import.meta.env.VITE_BACKEND_PORT || '8000';
    const localUrl = `${currentProtocol}//${currentHost}:${backendPort}`;
    console.log(`🔗 Using local backend: ${localUrl}`);
    return localUrl;
  }
  
  // 4. Production fallback
  const productionUrl = "https://triagz.com";
  console.log(`🔗 Using production URL: ${productionUrl}`);
  return productionUrl;
};

export const getWebSocketUrl = () => {
  // 1. Check if VITE_WS_URL is explicitly set
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }
  
  // 2. Detect based on API URL
  const apiUrl = getApiUrl();
  
  // Convert HTTP(S) to WS(S)
  if (apiUrl.startsWith('https://')) {
    return apiUrl.replace('https://', 'wss://');
  } else if (apiUrl.startsWith('http://')) {
    return apiUrl.replace('http://', 'ws://');
  }
  
  return apiUrl;
};

export const isLocalEnvironment = (hostname = window.location.hostname) => {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    hostname.startsWith('172.') ||
    hostname.endsWith('.local')
  );
};

export const isDevelopment = () => {
  return (
    import.meta.env.DEV ||
    import.meta.env.VITE_ENV === 'development' ||
    isLocalEnvironment()
  );
};

export const getEnvironmentInfo = () => {
  const apiUrl = getApiUrl();
  const wsUrl = getWebSocketUrl();
  const isLocal = isLocalEnvironment();
  const isDev = isDevelopment();
  
  return {
    apiUrl,
    wsUrl,
    isLocal,
    isDev,
    currentHost: window.location.hostname,
    currentPort: window.location.port,
    currentProtocol: window.location.protocol,
  };
};

// Log environment info on module load
if (isDevelopment()) {
  console.log('🔧 Triagz Frontend Environment:', getEnvironmentInfo());
}
